<template>
  <HrPageLayout
    title="Employees skills audits"
    subtitle="Review submissions within your data scope; export subsets, notify non-submitters, and apply supervisor or chief workflows."
    icon=""
    :breadcrumb-items="breadcrumbItems"
  >
    <div v-if="!allowed" class="saea-access-denied">
      <i class="bx bx-lock-alt"></i>
      You do not have access to this module.
    </div>

    <div v-else class="saea-shell">
      <section class="saea-hero">
        <p class="saea-lead">
          Operational console for county skills audit assessments: filter by year and workflow status,
          monitor coverage against active employees in scope, and open individual records for review,
          electronic sign-off, or return for correction.
        </p>
      </section>

      <div class="saea-filters-card">
        <div class="saea-filters-row">
          <div class="saea-field-group">
            <label class="saea-label">Audit year</label>
            <select v-model.number="filterYear" class="saea-select" @change="onYearChange">
              <option v-for="y in yearOpts" :key="'fy-' + y" :value="y">{{ y }}</option>
            </select>
          </div>
          <div class="saea-field-group">
            <label class="saea-label">Status</label>
            <select v-model="filterStatus" class="saea-select" @change="page = 1; loadList()">
              <option value="">All statuses</option>
              <option v-for="s in statuses" :key="s" :value="s">{{ formatStatusOption(s) }}</option>
            </select>
          </div>
          <div class="saea-filters-actions">
            <button type="button" class="saea-btn saea-btn--outline" :disabled="loading" @click="refreshAll">
              <i class="bx bx-refresh"></i>
              Refresh
            </button>
            <button type="button" class="saea-btn saea-btn--primary" title="Export all active employees in your data scope" @click="exportCsv('all')">
              <i class="bx bx-export"></i>
              Export all
            </button>
            <button type="button" class="saea-btn saea-btn--outline" :disabled="!exportDeptId" title="Requires department selected below" @click="exportCsv('department')">
              Export dept
            </button>
            <button type="button" class="saea-btn saea-btn--outline" :disabled="!exportEmpId" title="Requires employee DB id below" @click="exportCsv('employee')">
              Export ID
            </button>
            <button type="button" class="saea-btn saea-btn--warn" :disabled="notifyLoading || !stats || stats.not_submitted === 0" @click="notifyReminders">
              {{ notifyLoading ? "Sending…" : "Notify non-compliant" }}
            </button>
          </div>
        </div>
        <div class="saea-filters-row saea-filters-row--secondary">
          <div class="saea-field-grow">
            <label class="saea-label">Department (for export)</label>
            <select v-model.number="exportDeptId" class="saea-select saea-select--full">
              <option :value="0">— Select department —</option>
              <option v-for="d in departments" :key="'d-' + d.id" :value="Number(d.id)">{{ d.departmentName }}</option>
            </select>
          </div>
          <div class="saea-field-narrow">
            <label class="saea-label">Employee DB id</label>
            <input v-model.number="exportEmpId" type="number" min="1" class="saea-input" placeholder="e.g. 1024">
          </div>
          <div class="saea-field-grow">
            <label class="saea-label">Reminder message (optional)</label>
            <input v-model="reminderMsg" type="text" class="saea-input" placeholder="Extra text appended to default reminder">
          </div>
        </div>
      </div>

      <div v-if="stats" class="saea-metrics">
        <div class="saea-metric">
          <span class="saea-metric-label">Active (scope)</span>
          <span class="saea-metric-val">{{ stats.total_active_employees }}</span>
        </div>
        <div class="saea-metric saea-metric--success">
          <span class="saea-metric-label">Compliant</span>
          <span class="saea-metric-val">{{ stats.submitted_or_beyond_draft }}</span>
        </div>
        <div class="saea-metric saea-metric--warn">
          <span class="saea-metric-label">Not Compliant</span>
          <span class="saea-metric-val">{{ stats.not_submitted }}</span>
        </div>
        <div class="saea-metric saea-metric--info">
          <span class="saea-metric-label">Chief authorised</span>
          <span class="saea-metric-val">{{ stats.chief_authorised }}</span>
        </div>
      </div>

      <div class="saea-table-card">
        <div v-if="loading" class="saea-loading"><i class="bx bx-loader-alt bx-spin"></i> Loading assessments…</div>
        <div v-else class="saea-table-scroll">
          <table class="saea-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Employee</th>
                <th>Emp. No.</th>
                <th>Department</th>
                <th>Status</th>
                <th>Updated</th>
                <th class="saea-th-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in rows" :key="r.id">
                <td>{{ r.audit_year }}</td>
                <td class="saea-strong">{{ displayName(r) }}</td>
                <td>{{ r.empNo || "–" }}</td>
                <td>{{ r.departmentName || "–" }}</td>
                <td><span class="saea-pill" :class="statusPillClass(r.status)">{{ formatStatusOption(r.status) }}</span></td>
                <td class="saea-muted">{{ formatDtShort(r.updated_at) }}</td>
                <td class="saea-td-actions">
                  <button type="button" class="saea-btn saea-btn--sm saea-btn--ghost" @click="openDetail(r)">View</button>
                  <button type="button" class="saea-btn saea-btn--sm saea-btn--outline" @click="exportCsv('employee', r.employee_id)">CSV</button>
                </td>
              </tr>
              <tr v-if="!rows.length">
                <td colspan="7" class="saea-empty">No assessment records for the selected filters.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="total > limit" class="saea-pagination">
          <button type="button" class="saea-btn saea-btn--sm saea-btn--outline" :disabled="page <= 1" @click="page--; loadList()">
            Previous
          </button>
          <span class="saea-page-info">Page {{ page }} / {{ totalPages }}</span>
          <button type="button" class="saea-btn saea-btn--sm saea-btn--outline" :disabled="page >= totalPages" @click="page++; loadList()">
            Next
          </button>
        </div>
      </div>

      <!-- Detail modal -->
      <div v-if="showModal" class="saea-modal-overlay" @click.self="showModal = false">
        <div class="saea-modal saea-modal--lg" role="dialog" aria-modal="true">
          <div class="saea-modal-head">
            <div class="saea-modal-head-text">
              <h3 class="saea-modal-title">Assessment #{{ detail?.record?.id }}</h3>
              <p class="saea-modal-sub">{{ displayName(detail?.record) }} · {{ detail?.record?.empNo }} · {{ detail?.record?.nationalId }}</p>
              <p class="saea-modal-meta">
                Status:
                <span class="saea-pill saea-pill--inline" :class="statusPillClass(detail?.record?.status)">{{ formatStatusOption(detail?.record?.status) }}</span>
              </p>
              <p v-if="detail?.record?.return_reason" class="saea-return-reason">
                <strong>Return reason:</strong> {{ detail.record.return_reason }}
              </p>
            </div>
            <button type="button" class="saea-modal-close" aria-label="Close" @click="showModal = false">×</button>
          </div>

          <nav class="saea-tab-bar" aria-label="Assessment views">
            <button type="button" class="saea-tab" :class="{ 'saea-tab--active': tab === 'form' }" @click="tab = 'form'">
              Submitted form
            </button>
            <button
              v-if="detail?.permissions?.return_for_edit"
              type="button"
              class="saea-tab saea-tab--accent"
              :class="{ 'saea-tab--active': tab === 'ret' }"
              @click="tab = 'ret'"
            >
              Return for edit
            </button>
            <button
              v-if="detail?.permissions?.supervisor_sign"
              type="button"
              class="saea-tab"
              :class="{ 'saea-tab--active': tab === 'sup' }"
              @click="tab = 'sup'"
            >
              Supervisor sign-off
            </button>
            <button
              v-if="detail?.permissions?.chief_sign"
              type="button"
              class="saea-tab"
              :class="{ 'saea-tab--active': tab === 'chief' }"
              @click="tab = 'chief'"
            >
              Chief authorisation
            </button>
          </nav>

          <div class="saea-modal-body">
            <div v-show="tab === 'form'" class="saea-detail-scroll">
              <SkillsAuditAssessmentViewer :form="detail?.form" />
            </div>

            <div v-show="tab === 'ret'" class="saea-panel-block">
              <p class="saea-panel-note">The employee may edit and resubmit. Prior supervisor/chief attestations are cleared when they resubmit.</p>
              <label class="saea-label">Reason (optional)</label>
              <textarea v-model="returnReason" class="saea-textarea" rows="3"></textarea>
              <button type="button" class="saea-btn saea-btn--warn saea-mt" :disabled="actionLoading" @click="saveReturnForEdit">
                Return for employee to edit
              </button>
            </div>

            <div v-show="tab === 'sup'" class="saea-panel-block">
              <label class="saea-label">Name</label>
              <input v-model="sup.name" class="saea-input saea-input--full">
              <label class="saea-label">Designation</label>
              <input v-model="sup.designation" class="saea-input saea-input--full">
              <label class="saea-label">Date</label>
              <input v-model="sup.date" type="date" class="saea-input saea-input--full">
              <label class="saea-label">Signature</label>
              <textarea v-model="sup.signature" class="saea-textarea" rows="2"></textarea>
              <button type="button" class="saea-btn saea-btn--primary saea-mt" :disabled="actionLoading" @click="saveSupervisor">
                Record supervisor sign-off
              </button>
            </div>

            <div v-show="tab === 'chief'" class="saea-panel-block">
              <label class="saea-label">Name</label>
              <input v-model="chief.name" class="saea-input saea-input--full">
              <label class="saea-label">Designation</label>
              <input v-model="chief.designation" class="saea-input saea-input--full">
              <label class="saea-label">Date</label>
              <input v-model="chief.date" type="date" class="saea-input saea-input--full">
              <label class="saea-label">Date / official stamp (note)</label>
              <input v-model="chief.stamp" class="saea-input saea-input--full">
              <label class="saea-label">Signature</label>
              <textarea v-model="chief.signature" class="saea-textarea" rows="2"></textarea>
              <button type="button" class="saea-btn saea-btn--primary saea-mt" :disabled="actionLoading" @click="saveChief">
                Record authorisation
              </button>
            </div>

            <p v-if="actionMsg" class="saea-action-msg" :class="actionOk ? 'saea-action-msg--ok' : 'saea-action-msg--bad'">{{ actionMsg }}</p>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import SkillsAuditAssessmentViewer from "@/components/modules/SkillsAuditAssessmentViewer.vue";
import { authUrl, execute, executeDownload, getRight } from "@/api";

export default {
  name: "ModuleEmployeesSkillsAudits",
  components: { HrPageLayout, SkillsAuditAssessmentViewer },
  data() {
    const y = new Date().getFullYear();
    return {
      allowed: false,
      loading: false,
      notifyLoading: false,
      rows: [],
      total: 0,
      page: 1,
      limit: 30,
      filterYear: y,
      filterStatus: "",
      yearOpts: [y, y - 1, y - 2],
      statuses: ["Draft", "Submitted", "Supervisor_Signed", "Chief_Authorised", "Returned"],
      stats: null,
      departments: [],
      exportDeptId: 0,
      exportEmpId: null,
      reminderMsg: "",
      showModal: false,
      detail: null,
      tab: "form",
      actionLoading: false,
      actionMsg: "",
      actionOk: false,
      returnReason: "",
      sup: { name: "", designation: "", date: "", signature: "" },
      chief: { name: "", designation: "", date: "", stamp: "", signature: "" }
    };
  },
  computed: {
    breadcrumbItems() {
      return [
        { label: "Home", route: "/dashboard" },
        { label: "County HRMIS (SR)" },
        { label: "Skills Audit" },
        { label: "Employees skills audits" }
      ];
    },
    totalPages() {
      return this.limit > 0 ? Math.ceil(this.total / this.limit) : 1;
    }
  },
  created() {
    this.allowed = getRight("EMPLOYEES SKILLS AUDITS");
    if (this.allowed) {
      this.fetchDepartments();
      this.refreshAll();
    }
  },
  methods: {
    formatStatusOption(s) {
      return String(s || "").replace(/_/g, " ");
    },
    statusPillClass(status) {
      const k = String(status || "").toLowerCase();
      if (k.includes("chief")) return "saea-pill--chief";
      if (k.includes("supervisor")) return "saea-pill--sup";
      if (k.includes("return")) return "saea-pill--returned";
      if (k.includes("submit")) return "saea-pill--submitted";
      if (k.includes("draft")) return "saea-pill--draft";
      return "saea-pill--muted";
    },
    formatDtShort(v) {
      if (v == null || v === "") return "—";
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    },
    displayName(r) {
      if (!r) return "";
      const a = [r.firstName, r.middleName, r.lastName].filter(Boolean).join(" ").trim();
      return a || "—";
    },
    onYearChange() {
      this.page = 1;
      this.loadList();
      this.loadStats();
    },
    async refreshAll() {
      await Promise.all([this.loadList(), this.loadStats()]);
    },
    fetchDepartments() {
      const fd = new FormData();
      fd.append("function", "getDepartments");
      execute(fd, authUrl).then((res) => {
        const payload = res.data?.data || {};
        const d = payload.departments || payload;
        this.departments = Array.isArray(d) ? d : [];
      }).catch(() => { this.departments = []; });
    },
    async loadStats() {
      try {
        const fd = new FormData();
        fd.append("function", "getSkillsAuditAssessmentStats");
        fd.append("audit_year", String(this.filterYear));
        const res = await execute(fd, authUrl);
        this.stats = res.data?.success ? res.data.data : null;
      } catch (_) {
        this.stats = null;
      }
    },
    async loadList() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "listSkillsAuditAssessments");
        fd.append("limit", String(this.limit));
        fd.append("page", String(this.page));
        if (this.filterYear) fd.append("audit_year", String(this.filterYear));
        if (this.filterStatus) fd.append("status", this.filterStatus);
        const res = await execute(fd, authUrl);
        const d = res.data?.success ? res.data.data : {};
        this.rows = Array.isArray(d.data) ? d.data : [];
        this.total = Number(d.total || 0);
      } catch (_) {
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    async exportCsv(scope, empIdOverride) {
      const fd = new FormData();
      fd.append("function", "exportSkillsAuditAssessments");
      fd.append("audit_year", String(this.filterYear));
      fd.append("scope", scope);
      if (scope === "department" && this.exportDeptId) fd.append("department_id", String(this.exportDeptId));
      const eid = empIdOverride != null ? empIdOverride : this.exportEmpId;
      if (scope === "employee" && eid) fd.append("employee_id", String(eid));
      try {
        const res = await executeDownload(fd, authUrl);
        const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `skills-audit-${this.filterYear}-${scope}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        alert("Export failed.");
      }
    },
    async notifyReminders() {
      if (!this.stats || this.stats.not_submitted < 1) return;
      if (!confirm(`Send in-app reminder to employees who have not submitted for ${this.filterYear}? (${this.stats.not_submitted} in scope)`)) return;
      this.notifyLoading = true;
      try {
        const fd = new FormData();
        fd.append("function", "notifyNonSubmittersSkillsAuditAssessment");
        fd.append("audit_year", String(this.filterYear));
        if (this.reminderMsg.trim()) fd.append("message", this.reminderMsg.trim());
        const res = await execute(fd, authUrl);
        if (res.data?.success) {
          const n = res.data.data?.notified;
          alert(res.data.message || `Notified ${n != null ? n : 0} employee(s).`);
          await this.loadStats();
        } else {
          alert(res.data?.message || "Failed.");
        }
      } catch (_) {
        alert("Failed.");
      } finally {
        this.notifyLoading = false;
      }
    },
    async openDetail(row) {
      this.actionMsg = "";
      this.detail = null;
      this.tab = "form";
      this.returnReason = "";
      const fd = new FormData();
      fd.append("function", "getSkillsAuditAssessment");
      fd.append("id", String(row.id));
      const res = await execute(fd, authUrl);
      if (!res.data?.success) return;
      this.detail = res.data.data;
      this.showModal = true;
    },
    async saveReturnForEdit() {
      if (!this.detail?.record?.id) return;
      this.actionLoading = true;
      this.actionMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", "returnSkillsAuditAssessmentForEdit");
        fd.append("id", String(this.detail.record.id));
        fd.append("return_reason", this.returnReason);
        const res = await execute(fd, authUrl);
        this.actionOk = !!res.data?.success;
        this.actionMsg = res.data?.message || "";
        if (this.actionOk) {
          this.showModal = false;
          await this.refreshAll();
        }
      } catch (_) {
        this.actionOk = false;
        this.actionMsg = "Failed.";
      } finally {
        this.actionLoading = false;
      }
    },
    async saveSupervisor() {
      if (!this.detail?.record?.id) return;
      this.actionLoading = true;
      this.actionMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", "supervisorSignSkillsAuditAssessment");
        fd.append("id", String(this.detail.record.id));
        fd.append("supervisor_name", this.sup.name);
        fd.append("supervisor_designation", this.sup.designation);
        fd.append("supervisor_date", this.sup.date);
        fd.append("supervisor_signature", this.sup.signature);
        const res = await execute(fd, authUrl);
        this.actionOk = !!res.data?.success;
        this.actionMsg = res.data?.message || "";
        if (this.actionOk) {
          this.showModal = false;
          await this.refreshAll();
        }
      } catch (_) {
        this.actionOk = false;
        this.actionMsg = "Failed.";
      } finally {
        this.actionLoading = false;
      }
    },
    async saveChief() {
      if (!this.detail?.record?.id) return;
      this.actionLoading = true;
      this.actionMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", "chiefSignSkillsAuditAssessment");
        fd.append("id", String(this.detail.record.id));
        fd.append("chief_name", this.chief.name);
        fd.append("chief_designation", this.chief.designation);
        fd.append("chief_date", this.chief.date);
        fd.append("chief_official_stamp", this.chief.stamp);
        fd.append("chief_signature", this.chief.signature);
        const res = await execute(fd, authUrl);
        this.actionOk = !!res.data?.success;
        this.actionMsg = res.data?.message || "";
        if (this.actionOk) {
          this.showModal = false;
          await this.refreshAll();
        }
      } catch (_) {
        this.actionOk = false;
        this.actionMsg = "Failed.";
      } finally {
        this.actionLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.saea-shell {
  max-width: 1280px;
  margin: 0 auto;
  padding-bottom: 40px;
}

.saea-access-denied {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 22px;
  border-radius: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 14px;
}

.saea-hero {
  background: linear-gradient(135deg, #0d4f73 0%, #1478b0 55%, #0f5980 100%);
  color: #fff;
  border-radius: 14px;
  padding: 20px 22px;
  margin-bottom: 20px;
  box-shadow: 0 10px 32px rgba(13, 79, 115, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.saea-lead {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  opacity: 0.95;
}

.saea-filters-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px 18px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(13, 79, 115, 0.06);
}

.saea-filters-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 14px 20px;
  justify-content: space-between;
}

.saea-filters-row--secondary {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
}

.saea-field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 140px;
}

.saea-field-grow {
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.saea-field-narrow {
  width: 160px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.saea-label {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
}

.saea-select,
.saea-input {
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 14px;
  color: #1e293b;
  background: #fff;
}

.saea-select:focus,
.saea-input:focus {
  outline: none;
  border-color: #2195d3;
  box-shadow: 0 0 0 3px rgba(33, 149, 211, 0.2);
}

.saea-select--full,
.saea-input--full {
  width: 100%;
}

.saea-filters-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  flex: 1;
}

.saea-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background 0.2s, box-shadow 0.2s;
}

.saea-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.saea-btn--primary {
  background: linear-gradient(135deg, #2195d3 0%, #0d4f73 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(33, 149, 211, 0.35);
}

.saea-btn--outline {
  background: #fff;
  color: #0d4f73;
  border: 2px solid #94a3b8;
}

.saea-btn--ghost {
  background: transparent;
  color: #0d4f73;
  border: 1px solid #cbd5e1;
}

.saea-btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
}

.saea-btn--warn {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.35);
}

.saea-btn--sm {
  padding: 6px 12px;
  font-size: 12px;
}

.saea-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.saea-metric {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 2px 10px rgba(13, 79, 115, 0.06);
}

.saea-metric--success {
  border-color: #bbf7d0;
  background: linear-gradient(180deg, #f0fdf4 0%, #fff 100%);
}

.saea-metric--warn {
  border-color: #fde68a;
  background: linear-gradient(180deg, #fffbeb 0%, #fff 100%);
}

.saea-metric--info {
  border-color: #bae6fd;
  background: linear-gradient(180deg, #f0f9ff 0%, #fff 100%);
}

.saea-metric-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
}

.saea-metric-val {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.saea-table-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 18px rgba(13, 79, 115, 0.07);
  overflow: hidden;
}

.saea-table-scroll {
  overflow-x: auto;
}

.saea-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.saea-table thead {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.saea-table th {
  text-align: left;
  padding: 12px 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
}

.saea-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.saea-table tbody tr:hover {
  background: #fafbfc;
}

.saea-strong {
  font-weight: 600;
  color: #0f172a;
}

.saea-muted {
  color: #64748b;
  font-size: 13px;
}

.saea-th-actions,
.saea-td-actions {
  width: 160px;
  text-align: right;
  white-space: nowrap;
}

.saea-td-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.saea-empty {
  text-align: center;
  color: #94a3b8;
  padding: 28px 16px !important;
}

.saea-loading {
  text-align: center;
  padding: 32px;
  color: #64748b;
}

.saea-pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
}

.saea-page-info {
  font-size: 13px;
  color: #64748b;
}

.saea-pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.saea-pill--inline {
  font-size: 12px;
  padding: 5px 12px;
}

.saea-pill--draft {
  background: #fef3c7;
  color: #92400e;
}

.saea-pill--submitted {
  background: #e0f2fe;
  color: #0369a1;
}

.saea-pill--sup {
  background: #ede9fe;
  color: #5b21b6;
}

.saea-pill--chief {
  background: #ecfdf5;
  color: #047857;
}

.saea-pill--returned {
  background: #fee2e2;
  color: #991b1b;
}

.saea-pill--muted {
  background: #f1f5f9;
  color: #64748b;
}

/* Modal */
.saea-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.saea-modal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 720px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e8f0;
  box-shadow: 0 24px 48px rgba(13, 79, 115, 0.2);
}

.saea-modal--lg {
  max-width: 920px;
}

.saea-modal-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
}

.saea-modal-title {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 700;
  color: #0d4f73;
}

.saea-modal-sub {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.saea-modal-meta {
  margin: 8px 0 0;
  font-size: 13px;
  color: #334155;
}

.saea-return-reason {
  margin: 10px 0 0;
  font-size: 13px;
  color: #b91c1c;
}

.saea-modal-close {
  border: none;
  background: #f1f5f9;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #475569;
}

.saea-modal-close:hover {
  background: #e2e8f0;
}

.saea-tab-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;
}

.saea-tab {
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
}

.saea-tab:hover {
  background: #fff;
  color: #0d4f73;
}

.saea-tab--active {
  background: #fff;
  color: #0d4f73;
  border-color: #bae6fd;
  box-shadow: 0 2px 8px rgba(13, 79, 115, 0.08);
}

.saea-tab--accent.saea-tab--active {
  border-color: #fcd34d;
  color: #92400e;
}

.saea-modal-body {
  padding: 16px 20px 20px;
  overflow-y: auto;
  flex: 1;
}

.saea-detail-scroll {
  max-height: min(520px, 65vh);
  overflow: auto;
}

.saea-panel-block {
  font-size: 14px;
}

.saea-panel-note {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 12px;
}

.saea-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
}

.saea-textarea:focus {
  outline: none;
  border-color: #2195d3;
  box-shadow: 0 0 0 3px rgba(33, 149, 211, 0.2);
}

.saea-mt {
  margin-top: 14px;
}

.saea-action-msg {
  margin: 14px 0 0;
  font-size: 13px;
}

.saea-action-msg--ok {
  color: #047857;
}

.saea-action-msg--bad {
  color: #b91c1c;
}
</style>
